import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DonorSummary, Paginated } from '@foundation/shared';
import { DONOR_TYPE_LABEL_TH, DONOR_TYPES } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatDate, formatSatang } from '../lib/format';
import { Empty, ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

export default function DonorList() {
  const { allowed } = useAuth();
  const [rows, setRows] = useState<DonorSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [q, setQ] = useState('');
  const [donorType, setDonorType] = useState('');
  const [sort, setSort] = useState<'recent' | 'total_amount' | 'name'>('recent');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api<Paginated<DonorSummary>>(
        `/donors${buildQuery({ q, donorType, sort, limit: 100 })}`,
      );
      setRows(res.data);
    } catch (e) {
      setError(e);
      setRows([]);
    }
  }, [q, donorType, sort]);

  useEffect(() => {
    const t = setTimeout(() => void load(), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>ผู้บริจาค</h1>
          <p>ยอดสะสมคำนวณจากรายการที่ยืนยันแล้วเท่านั้น</p>
        </div>
        {allowed('donor:write') && <button onClick={() => setShowCreate(true)}>+ เพิ่มผู้บริจาค</button>}
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field grow">
            <label htmlFor="donorq">ค้นหา</label>
            <input
              id="donorq"
              type="search"
              placeholder="ชื่อ รหัส เบอร์โทร หรืออีเมล"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="dtype">ประเภท</label>
            <select id="dtype" value={donorType} onChange={(e) => setDonorType(e.target.value)}>
              <option value="">ทั้งหมด</option>
              {DONOR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {DONOR_TYPE_LABEL_TH[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="dsort">เรียงตาม</label>
            <select
              id="dsort"
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
            >
              <option value="recent">ล่าสุด</option>
              <option value="total_amount">ยอดสะสมสูงสุด</option>
              <option value="name">ชื่อ</option>
            </select>
          </div>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>ยังไม่มีผู้บริจาคตามเงื่อนไขนี้</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th className="wrap">ชื่อ</th>
                  <th>ประเภท</th>
                  <th>เบอร์โทร</th>
                  <th>ยอดสะสม</th>
                  <th>ครั้ง</th>
                  <th>บริจาคล่าสุด</th>
                  <th>LINE</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td className="mono small">{d.donorCode}</td>
                    <td className="wrap">
                      <Link className="row-link" to={`/donors/${d.id}`}>
                        {d.displayName}
                      </Link>
                      {d.isAnonymous && <span className="badge" style={{ marginInlineStart: 6 }}>ไม่ออกนาม</span>}
                    </td>
                    <td className="small">{DONOR_TYPE_LABEL_TH[d.donorType] ?? d.donorType}</td>
                    <td>{d.phone ?? '—'}</td>
                    <td style={{ fontWeight: 600 }}>{formatSatang(d.totalAmount)}</td>
                    <td>{d.donationCount}</td>
                    <td className="small muted">{formatDate(d.lastDonatedAt)}</td>
                    <td>{d.lineLinked ? <span className="badge green">ผูกแล้ว</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateDonorModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function CreateDonorModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    donorType: 'individual',
    title: 'นาย',
    firstName: '',
    lastName: '',
    orgName: '',
    phone: '',
    email: '',
    taxId: '',
    addressLine: '',
    wantsReceipt: false,
    isAnonymous: false,
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const isOrg = form.donorType === 'corporate';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api('/donors', {
        method: 'POST',
        body: {
          donorType: form.donorType,
          title: isOrg ? undefined : form.title || undefined,
          firstName: isOrg ? undefined : form.firstName.trim() || undefined,
          lastName: isOrg ? undefined : form.lastName.trim() || undefined,
          orgName: isOrg ? form.orgName.trim() : undefined,
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
          taxId: form.taxId.trim() || undefined,
          addressLine: form.addressLine.trim() || undefined,
          wantsReceipt: form.wantsReceipt,
          isAnonymous: form.isAnonymous,
        },
      });
      onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <Modal title="เพิ่มผู้บริจาค" onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorAlert error={error} />

        <div className="field">
          <label htmlFor="ntype">ประเภท *</label>
          <select
            id="ntype"
            value={form.donorType}
            onChange={(e) => set('donorType', e.target.value)}
          >
            {DONOR_TYPES.map((t) => (
              <option key={t} value={t}>
                {DONOR_TYPE_LABEL_TH[t]}
              </option>
            ))}
          </select>
        </div>

        {isOrg ? (
          <div className="field">
            <label htmlFor="norg">ชื่อองค์กร *</label>
            <input
              id="norg"
              required
              value={form.orgName}
              onChange={(e) => set('orgName', e.target.value)}
            />
          </div>
        ) : (
          <div className="form-row">
            <div className="field">
              <label htmlFor="ntitle">คำนำหน้า</label>
              <select id="ntitle" value={form.title} onChange={(e) => set('title', e.target.value)}>
                {['นาย', 'นาง', 'นางสาว', ''].map((t) => (
                  <option key={t || 'none'} value={t}>
                    {t || 'ไม่ระบุ'}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="nfirst">ชื่อ</label>
              <input
                id="nfirst"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="nlast">นามสกุล</label>
              <input
                id="nlast"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="field">
            <label htmlFor="nphone">เบอร์โทร</label>
            <input
              id="nphone"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="0812345678"
            />
          </div>
          <div className="field">
            <label htmlFor="nemail">อีเมล</label>
            <input
              id="nemail"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="ntax">เลขประจำตัวผู้เสียภาษี</label>
          <input
            id="ntax"
            inputMode="numeric"
            maxLength={17}
            value={form.taxId}
            onChange={(e) => set('taxId', e.target.value)}
          />
          <div className="hint">เก็บแบบเข้ารหัส แสดงผลเพียง 4 ตัวท้าย — ใช้สำหรับใบอนุโมทนาบัตร</div>
        </div>

        <div className="field">
          <label htmlFor="naddr">ที่อยู่</label>
          <input
            id="naddr"
            value={form.addressLine}
            onChange={(e) => set('addressLine', e.target.value)}
          />
        </div>

        <label className="checkbox" style={{ marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={form.wantsReceipt}
            onChange={(e) => set('wantsReceipt', e.target.checked)}
          />
          <span>ต้องการใบอนุโมทนาบัตรทุกครั้ง</span>
        </label>
        <label className="checkbox" style={{ marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={form.isAnonymous}
            onChange={(e) => set('isAnonymous', e.target.checked)}
          />
          <span>ไม่ประสงค์ออกนามในรายงานสาธารณะ</span>
        </label>

        <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
